import {
    ALL_PROSPECT_PRACTICE_AREAS,
    type ProspectMatterType,
    type ProspectPracticeArea,
} from '@common/enums';
import type { RetainerAgreement } from '@common/types/retainerAgreements';
import type { UserEntry } from '@common/types/users';
import { getPracticeAreaLabel } from '@common/util';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import LoadingButton from '@/components/Buttons/LoadingButton';
import FormUploadField from '@/components/FormComponent/FormUploadField';
import { THIN_SCROLLBAR_STYLING } from '@/constants';
import { useLawFirm } from '@/queries/lawFirms';
import { createRetainerAgreement } from '@/services/LegalDocumentService';
import { getPracticeAreaMatterTypeOptions } from '@/utils/practiceAreaMatterTypes';
import { errorSnackbar, warningSnackbar } from '@/utils/snackbar';
import { ALL_USERS_LABEL, ALL_USERS_OPTION } from './constants';

const VALIDATION_SCHEMA = {
    fileCount: 1,
    fileSize: 10 * 1024 * 1024,
    fileType: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/pdf',
    ],
};

interface Props {
    users: UserEntry[];
    lawFirmId: number;
    /** Adds a whole-firm retainer for this practice area and matter type, such as a template's */
    fixedMatter?: { practiceArea: ProspectPracticeArea; matterType: ProspectMatterType };
    onCreated?(retainer: RetainerAgreement): void;
    onClose(): void;
}

export default function CreateRetainerModal({
    users,
    lawFirmId,
    fixedMatter,
    onCreated,
    onClose,
}: Props) {
    const queryClient = useQueryClient();
    const { data: lawFirm } = useLawFirm(lawFirmId);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [isCloseAlert, setIsCloseAlert] = useState(false);

    // Form data
    const [userId, setUserId] = useState<number | null>(null);
    const [fileIds, setFileIds] = useState<string[]>([]);
    const [practiceArea, setPracticeArea] = useState<ProspectPracticeArea | null>(
        fixedMatter?.practiceArea ?? null,
    );
    const [matterType, setMatterType] = useState<ProspectMatterType | null>(
        fixedMatter?.matterType ?? null,
    );
    const [documentDisplayName, setDocumentDisplayName] = useState('');

    // Error data
    const [fileIdError, setFileIdError] = useState<string | null>(null);
    const [practiceAreaError, setPracticeAreaError] = useState<string | null>(null);
    const [matterTypeError, setMatterTypeError] = useState<string | null>(null);
    const handleFileIdsChange = (ids: string[]) => {
        setFileIds(ids);
        if (ids.length > 0) {
            setFileIdError(null);
        }
    };

    const handleUserIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setUserId(value === ALL_USERS_OPTION ? null : Number(value));
    };

    const handlePracticeAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPracticeArea = event.target.value as ProspectPracticeArea;
        setPracticeArea(newPracticeArea);
        setPracticeAreaError(null);
        // Reset dependent fields when practice area changes
        setMatterType(null);
        setMatterTypeError(null);
        setFileIds([]);
    };

    const handleMatterTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newMatterType = event.target.value as ProspectMatterType;
        setMatterType(newMatterType);
        setMatterTypeError(null);
        // Reset dependent field when matter type changes
        setFileIds([]);
    };

    const practiceAreaMatterTypeOptions = getPracticeAreaMatterTypeOptions({
        lawFirmId,
        isTestFirm: lawFirm?.isTestFirm ?? false,
    });

    const matterTypeOptions = practiceArea ? practiceAreaMatterTypeOptions[practiceArea] : [];

    const validate = (): boolean => {
        if (!practiceArea) {
            setPracticeAreaError('This field is required');
            return false;
        }
        if (!matterType) {
            setMatterTypeError('This field is required');
            return false;
        }
        if (fileIds.length === 0) {
            setFileIdError('This field is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        setHasAttemptedSubmit(true);

        if (!validate()) {
            warningSnackbar('Please fill all required fields.', true);
            return;
        }

        try {
            setIsSubmitting(true);
            const retainer = await createRetainerAgreement({
                userId,
                practiceArea: practiceArea as ProspectPracticeArea,
                matterType: matterType as ProspectMatterType,
                province: 'Ontario',
                fileId: fileIds[0],
                lawFirmId,
                documentDisplayName: documentDisplayName.trim() || null,
            });
            await queryClient.invalidateQueries({ queryKey: ['retainerAgreements'] });
            onCreated?.(retainer);
            onClose();
        } catch (error) {
            console.error(error);
            errorSnackbar('An error occurred creating the document');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        const hasChosenMatter =
            !fixedMatter && (userId !== null || practiceArea !== null || matterType !== null);
        if (!hasChosenMatter && fileIds.length === 0 && !documentDisplayName.trim()) {
            onClose();
        } else {
            setIsCloseAlert(true);
        }
    };

    return (
        <Dialog open maxWidth={false} PaperProps={{ sx: { width: '50vw' } }}>
            <DialogTitle>Add Document</DialogTitle>
            <DialogContent sx={THIN_SCROLLBAR_STYLING}>
                <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            Available To
                        </Typography>
                        <TextField
                            name="userId"
                            select
                            sx={{ width: '100%', mt: 1 }}
                            value={userId ?? ALL_USERS_OPTION}
                            onChange={handleUserIdChange}
                            label="Available To"
                            variant="outlined"
                            disabled={!!fixedMatter}
                        >
                            <MenuItem value={ALL_USERS_OPTION}>{ALL_USERS_LABEL}</MenuItem>
                            {users.map((user) => (
                                <MenuItem key={user?.userId} value={user?.userId}>
                                    {user?.fullName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            Select Practice Area
                        </Typography>
                        <TextField
                            name="practiceArea"
                            select
                            sx={{ width: '100%', mt: 1 }}
                            value={practiceArea ?? ''}
                            onChange={handlePracticeAreaChange}
                            label="Select Practice Area"
                            variant="outlined"
                            disabled={!!fixedMatter}
                            error={hasAttemptedSubmit && !!practiceAreaError}
                            helperText={hasAttemptedSubmit && practiceAreaError}
                        >
                            {ALL_PROSPECT_PRACTICE_AREAS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {getPracticeAreaLabel(option)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            Select Matter Type
                        </Typography>
                        <TextField
                            name="matterType"
                            select
                            sx={{ width: '100%', mt: 1 }}
                            value={matterType ?? ''}
                            onChange={handleMatterTypeChange}
                            label="Select Matter Type"
                            variant="outlined"
                            disabled={!practiceArea || !!fixedMatter}
                            error={hasAttemptedSubmit && !!matterTypeError}
                            helperText={hasAttemptedSubmit && matterTypeError}
                        >
                            {matterTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="labelLarge" component="p">
                            PDF download name (optional)
                        </Typography>
                        <TextField
                            name="documentDisplayName"
                            sx={{ width: '100%', mt: 1 }}
                            value={documentDisplayName}
                            onChange={(event) => setDocumentDisplayName(event.target.value)}
                            label="Custom name for PDF downloads"
                            variant="outlined"
                            helperText="Leave blank for default names (Legal Document / Signed Legal Document). When set, used as the prefix for unsigned and signed PDF downloads."
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={3} mt={1}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="labelLarge" component="p">
                            Upload Legal Document
                        </Typography>
                        <FormUploadField
                            typeofDocument="Retainer Agreement"
                            name="retainer-agreement-file"
                            fileIds={fileIds}
                            selectedLawFirmId={lawFirmId}
                            onFileIdsChange={handleFileIdsChange}
                            deleteOnRemove
                            validationSchema={VALIDATION_SCHEMA}
                            isError={hasAttemptedSubmit && !!fileIdError}
                            disabled={!practiceArea || !matterType}
                        />
                        {hasAttemptedSubmit && fileIdError && (
                            <FormHelperText error>{fileIdError}</FormHelperText>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isSubmitting}
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        Submit
                    </LoadingButton>
                </Stack>
            </DialogActions>
            <AlertDialog
                open={isCloseAlert}
                handleClose={() => setIsCloseAlert(false)}
                handleConfirm={onClose}
                title="Are you sure?"
                message="Closing the popup will result in loss of any data that you have filled up. Are you sure you want to close the popup?"
            />
        </Dialog>
    );
}
